import React, { useEffect, useState } from "react";
import { Ban, Download, FileSpreadsheet, Loader2, Mail, Pencil, Plus, Search, X } from "lucide-react";
import { toast } from "sonner";
import api, { apiError, downloadFile } from "../api";
import { fmtDate, useLang } from "../i18n";
import { EmailReportButton } from "../components/EmailReportButton";
import { DynRows, Field } from "../components/fields";
import DateInput from "../components/DateInput";

const EMPTY = { subject_type: "person", name: "", id_number: "", ruc: "", directors: [], reason: "", inclusion_date: new Date().toISOString().slice(0, 10), verif_status: "positivo_confirmado", verif_notes: "", photo_base64: "" };
const EMPTY_DOCS = { cedula: null, pasaporte: null, licencia: null };

export default function Blacklist() {
  const { t } = useLang();
  const [items, setItems] = useState(null);
  const [q, setQ] = useState("");
  const [form, setForm] = useState(EMPTY);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [att, setAtt] = useState({ filename: "", note: "", file_base64: null });
  const [selected, setSelected] = useState(null);
  const selectedRow = (items || []).find((x) => x.id === selected) || null;
  const [docs, setDocs] = useState(EMPTY_DOCS);

  const onPhoto = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 800_000) return toast.error(t("k.file_optional"));
    const rd = new FileReader();
    rd.onload = () => setForm((p) => ({ ...p, photo_base64: rd.result }));
    rd.readAsDataURL(f);
  };

  const onDoc = (k) => (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 1_500_000) return toast.error(t("k.file_optional"));
    const rd = new FileReader();
    rd.onload = () => setDocs((p) => ({ ...p, [k]: { filename: f.name, file_base64: rd.result } }));
    rd.readAsDataURL(f);
  };


  const load = () => api.get("/blacklist", { params: q ? { q } : {} }).then((r) => setItems(r.data)).catch(() => setItems([]));
  useEffect(() => { load(); }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const documents = Object.entries(docs).filter(([, v]) => v?.file_base64).map(([k, v]) => ({ doc_type: t(`bl.doc_${k}`), filename: v.filename, file_base64: v.file_base64 }));
      if (editing) {
        await api.patch(`/blacklist/${editing.id}`, form);
        toast.success(t("bl.updated"));
      } else {
        await api.post("/blacklist", { ...form, documents });
        toast.success(t("bl.saved"));
      }
      setShowForm(false); setEditing(null); setForm(EMPTY); setDocs(EMPTY_DOCS); load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  const toggleStatus = async (item) => {
    try {
      await api.patch(`/blacklist/${item.id}`, { status: item.status === "activo" ? "inactivo" : "activo" });
      toast.success(t("bl.updated"));
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const openEdit = (item) => {
    setForm({ subject_type: item.subject_type, name: item.name, id_number: item.id_number || "", ruc: item.ruc || "", directors: item.directors || [], reason: item.reason || "", inclusion_date: item.inclusion_date || "", verif_status: item.verif_status || "positivo_confirmado", verif_notes: item.verif_notes || "", photo_base64: item.photo_base64 || "" });
    setEditing(item);
    setShowForm(true);
  };

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return setAtt({ ...att, file_base64: null });
    if (f.size > 1_500_000) return toast.error(t("k.file_optional"));
    const rd = new FileReader();
    rd.onload = () => setAtt((p) => ({ ...p, file_base64: rd.result, filename: p.filename || f.name }));
    rd.readAsDataURL(f);
  };

  const addAttachment = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/blacklist/${editing.id}/attachments`, att);
      toast.success(t("k.att_added"));
      setAtt({ filename: "", note: "", file_base64: null });
      const { data } = await api.get("/blacklist");
      const fresh = data.find((x) => x.id === editing.id);
      if (fresh) setEditing(fresh);
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const TYPE_LABEL = { person: "bl.t_person", company: "bl.t_company", supplier: "bl.t_supplier" };
  const typeCls = { person: "bg-orange-100 text-[#033A8C]", company: "bg-indigo-100 text-indigo-800", supplier: "bg-amber-100 text-amber-800" };

  return (
    <div className="space-y-6" data-testid="blacklist-page">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900 flex items-center gap-3">
            <Ban className="w-7 h-7 text-[#044CBC]" /> {t("bl.title")}
          </h1>
          <p className="text-sm text-slate-500 mt-1 max-w-3xl">{t("bl.subtitle")}</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => downloadFile("/export/compliance.pdf?kind=blacklist", "lista_negra_kycfederal.pdf")} data-testid="bl-export-pdf" className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[#033A8C] text-white text-sm font-bold hover:bg-[#16335F]">
            <Download className="w-4 h-4" /> {t("common.export_pdf")}
          </button>
          <button onClick={() => downloadFile("/export/compliance.xlsx?kind=blacklist", "lista_negra_kycfederal.xlsx")} data-testid="bl-export-excel" className="flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-700 text-white text-sm font-bold hover:bg-emerald-800">
            <FileSpreadsheet className="w-4 h-4" /> {t("common.download_excel")}
          </button>
          <button onClick={() => { setShowForm(true); setEditing(null); setForm(EMPTY); setDocs(EMPTY_DOCS); }} data-testid="bl-add-button" className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[#16335F] text-white text-sm font-bold hover:bg-[#033A8C] transition-colors">
            <Plus className="w-4 h-4" /> {t("bl.new")}
          </button>
        </div>
      </div>

      <div className="ec-card p-4 flex gap-3 items-center">
        <input className="ec-input max-w-md" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("bl.search_ph")} data-testid="bl-search-input" onKeyDown={(e) => e.key === "Enter" && load()} />
        <button onClick={load} data-testid="bl-search-button" className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[#16335F] text-white text-sm font-bold"><Search className="w-4 h-4" />{t("common.search")}</button>
      </div>

      {selectedRow && (
        <div className="flex flex-wrap items-center gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3" data-testid="bl-selected-bar">
          <span className="text-[11px] font-extrabold px-2 py-0.5 rounded bg-red-600 text-white" data-testid="bl-selected-badge">Lista Negra (BL)</span>
          <span className="text-sm font-bold text-slate-800 flex-1 min-w-[200px]">{selectedRow.name} — {selectedRow.reason || "—"}</span>
          <button onClick={() => downloadFile(`/blacklist/${selectedRow.id}/pdf`, `lista_negra_bl_${selectedRow.id_number || selectedRow.id}.pdf`)} data-testid="bl-record-pdf" className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-[#044CBC] text-white text-sm font-bold hover:bg-[#033A8C] transition-colors">
            <Download className="w-4 h-4" /> {t("search.pdf")}
          </button>
          <EmailReportButton pdfPath={`/blacklist/${selectedRow.id}/pdf`} filename={`lista_negra_bl_${selectedRow.id_number || selectedRow.id}.pdf`} subject={`Ficha Lista Negra Privada — ${selectedRow.name}`} module="Lista Negra Privada" summary={`${selectedRow.name} — ${selectedRow.reason || ""}`} testid="bl-record-email" variant="outline" className="!text-sm !px-3" />
          <button onClick={() => setSelected(null)} data-testid="bl-clear-selection" className="text-slate-400 hover:text-slate-700" title={t("bl.clear_sel")}>
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      <div className="ec-card overflow-hidden" data-testid="bl-table">
        {items === null ? (
          <div className="p-10 text-center text-sm text-slate-500"><Loader2 className="w-5 h-5 animate-spin inline mr-2" />{t("common.loading")}</div>
        ) : items.length === 0 ? (
          <div className="p-10 text-center text-sm text-slate-400" data-testid="bl-empty">{t("h.empty")}</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[960px]">
              <thead>
                <tr className="bg-[#16335F] text-white text-left">
                  <th className="px-4 py-3 text-xs font-bold uppercase tracking-wide">{t("bl.type")}</th>
                  <th className="px-4 py-3 text-xs font-bold uppercase tracking-wide">{t("bl.name")}</th>
                  <th className="px-4 py-3 text-xs font-bold uppercase tracking-wide">{t("bl.id_number")} / RUC</th>
                  <th className="px-4 py-3 text-xs font-bold uppercase tracking-wide">{t("bl.reason")}</th>
                  <th className="px-4 py-3 text-xs font-bold uppercase tracking-wide">{t("bl.inclusion_date")}</th>
                  <th className="px-4 py-3 text-xs font-bold uppercase tracking-wide">{t("bl.registered_by")}</th>
                  <th className="px-4 py-3 text-xs font-bold uppercase tracking-wide">{t("bl.status")}</th>
                  <th className="px-4 py-3 text-xs font-bold uppercase tracking-wide text-right">{t("common.actions")}</th>
                </tr>
              </thead>
              <tbody>
                {items.map((it) => (
                  <tr key={it.id} onClick={() => setSelected(selected === it.id ? null : it.id)} className={`border-t border-slate-100 cursor-pointer ${selected === it.id ? "bg-red-50" : "hover:bg-slate-50"}`} data-testid={`bl-row-${it.id}`}>
                    <td className="px-4 py-2.5"><span className={`text-[11px] font-bold px-2 py-0.5 rounded ${typeCls[it.subject_type]}`}>{t(TYPE_LABEL[it.subject_type])}</span></td>
                    <td className="px-4 py-2.5 font-semibold text-slate-800">
                      {it.photo_base64 && <img src={it.photo_base64} alt="" className="mb-1 h-10 w-10 object-cover rounded-lg border border-slate-200" data-testid={`bl-photo-${it.id}`} />}
                      {it.name}
                      {selected === it.id && <span className="ml-2 text-[10px] font-extrabold px-1.5 py-0.5 rounded bg-red-600 text-white align-middle" data-testid={`bl-mark-${it.id}`}>Lista Negra (BL)</span>}
                      {it.verif_status && (
                        <div className="mt-0.5">
                          <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${{ negativo: "bg-emerald-50 text-emerald-700", falso_positivo: "bg-slate-100 text-slate-500", en_revision: "bg-amber-50 text-amber-700", positivo_confirmado: "bg-red-50 text-red-700" }[it.verif_status]}`} data-testid={`bl-verif-${it.id}`}>
                            {t(`vs.${it.verif_status}`)}
                          </span>
                        </div>
                      )}
                      {(it.directors || []).length > 0 && <div className="text-[10px] font-normal text-slate-400">{it.directors.length} {t("bl.directors").toLowerCase()}</div>}
                    </td>
                    <td className="px-4 py-2.5 text-xs font-mono text-slate-600">{[it.id_number, it.ruc].filter(Boolean).join(" / ") || "—"}</td>
                    <td className="px-4 py-2.5 text-xs text-slate-600 max-w-[220px] truncate">{it.reason}</td>
                    <td className="px-4 py-2.5 text-xs text-slate-500">{it.inclusion_date}</td>
                    <td className="px-4 py-2.5 text-xs text-slate-600">{it.created_by}{it.updated_by && it.updated_by !== it.created_by && <div className="text-[10px] text-slate-400">{t("bl.updated_by")}: {it.updated_by}</div>}</td>
                    <td className="px-4 py-2.5"><span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${it.status === "activo" ? "bg-red-50 text-red-700" : "bg-slate-100 text-slate-500"}`} data-testid={`bl-status-${it.id}`}>{it.status === "activo" ? t("bl.active") : t("bl.inactive")}</span></td>
                    <td className="px-4 py-2.5 text-right space-x-3" onClick={(e) => e.stopPropagation()}>
                      <button onClick={() => openEdit(it)} data-testid={`bl-edit-${it.id}`} className="text-slate-600 hover:text-slate-900 text-xs font-bold inline-flex items-center gap-1"><Pencil className="w-3.5 h-3.5" />{t("bl.edit")}</button>
                      <button onClick={() => toggleStatus(it)} data-testid={`bl-toggle-${it.id}`} className={`text-xs font-bold ${it.status === "activo" ? "text-red-600" : "text-emerald-600"}`}>
                        {it.status === "activo" ? t("bl.deactivate") : t("bl.activate")}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showForm && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setShowForm(false)}>
          <form onSubmit={submit} className="ec-card max-w-2xl w-full max-h-[88vh] overflow-y-auto p-6 space-y-4" onClick={(e) => e.stopPropagation()} data-testid="bl-form">
            <h3 className="font-heading font-extrabold text-slate-900">{editing ? t("bl.edit") : t("bl.new")}</h3>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label={t("bl.type")}>
                <select className="ec-input" value={form.subject_type} onChange={set("subject_type")} data-testid="bl-type">
                  <option value="person">{t("bl.t_person")}</option>
                  <option value="company">{t("bl.t_company")}</option>
                  <option value="supplier">{t("bl.t_supplier")}</option>
                </select>
              </Field>
              <Field label={t("bl.inclusion_date")}><DateInput className="ec-input" value={form.inclusion_date} onChange={set("inclusion_date")} data-testid="bl-inclusion-date" /></Field>
              <Field label={t("bl.name")} className="sm:col-span-2"><input className="ec-input" value={form.name} onChange={set("name")} required data-testid="bl-name" /></Field>
              <Field label={t("bl.id_number")}><input className="ec-input" value={form.id_number} onChange={set("id_number")} data-testid="bl-id-number" /></Field>
              <Field label={t("bl.ruc")}><input className="ec-input" value={form.ruc} onChange={set("ruc")} data-testid="bl-ruc" /></Field>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label={t("bl.photo")}>
                <input className="ec-input" type="file" accept="image/*" onChange={onPhoto} data-testid="bl-photo-input" />
                {form.photo_base64 && <img src={form.photo_base64} alt="foto" className="mt-2 h-16 w-16 object-cover rounded-lg border border-slate-200" data-testid="bl-photo-preview" />}
              </Field>
              {[["cedula", "bl.doc_cedula"], ["pasaporte", "bl.doc_pasaporte"], ["licencia", "bl.doc_licencia"]].map(([k, label]) => (
                <Field key={k} label={t(label)}>
                  <input className="ec-input" type="file" onChange={onDoc(k)} data-testid={`bl-doc-${k}`} />
                  {docs[k]?.filename && <div className="text-[11px] text-slate-500 mt-1">{docs[k].filename}</div>}
                </Field>
              ))}
            </div>
            <Field label={t("bl.directors")}>
              <DynRows testid="bl-directors" columns={[{ key: "name", label: t("bl.dir_name") }, { key: "id_number", label: t("bl.dir_id") }, { key: "position", label: t("bl.dir_position") }]} rows={form.directors} onChange={(v) => setForm({ ...form, directors: v })} />
            </Field>
            <Field label={t("bl.reason")}><textarea className="ec-input" rows={2} value={form.reason} onChange={set("reason")} data-testid="bl-reason" /></Field>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label={t("vs.label")}>
                <select className="ec-input" value={form.verif_status} onChange={set("verif_status")} data-testid="bl-verif-status">
                  {["negativo", "falso_positivo", "en_revision", "positivo_confirmado"].map((s) => <option key={s} value={s}>{t(`vs.${s}`)}</option>)}
                </select>
                <p className="text-[11px] text-slate-400 mt-1">{t(`vs.d_${form.verif_status}`)}</p>
              </Field>
              <Field label={t("vs.notes")}>
                <textarea className="ec-input" rows={2} value={form.verif_notes} onChange={set("verif_notes")} placeholder={t("vs.notes_ph")} data-testid="bl-verif-notes" />
              </Field>
            </div>
            {editing && (
              <div className="pt-3 border-t border-slate-100">
                <h4 className="ec-label">{t("bl.documents")}</h4>
                <ul className="space-y-1.5 mb-2">
                  {(editing.attachments || []).map((a) => (
                    <li key={a.id} className="flex items-center justify-between text-xs bg-slate-50 rounded px-2.5 py-1.5">
                      <span className="font-semibold text-slate-700">{a.filename} <span className="font-normal text-slate-400">— {a.uploaded_by} · {fmtDate(a.date)}</span></span>
                      <button type="button" onClick={() => downloadFile(`/blacklist/${editing.id}/attachments/${a.id}/download`, a.filename)} className="text-[#044CBC]" data-testid={`bl-att-dl-${a.id}`}><Download className="w-3.5 h-3.5" /></button>
                    </li>
                  ))}
                </ul>
                <div className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto] gap-2 items-end">
                  <input className="ec-input" placeholder={t("k.filename")} value={att.filename} onChange={(e) => setAtt({ ...att, filename: e.target.value })} data-testid="bl-att-filename" />
                  <input className="ec-input" type="file" onChange={onFile} data-testid="bl-att-file" />
                  <button type="button" onClick={addAttachment} className="px-3.5 py-2 rounded-lg bg-[#16335F] text-white text-xs font-bold" data-testid="bl-att-submit">{t("k.add_attachment")}</button>
                </div>
              </div>
            )}
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" onClick={() => setShowForm(false)} className="px-4 py-2 text-sm font-semibold text-slate-600">{t("common.cancel")}</button>
              <button type="submit" disabled={busy} data-testid="bl-submit" className="px-5 py-2 rounded-lg bg-[#16335F] text-white text-sm font-bold hover:bg-[#033A8C] disabled:opacity-60 flex items-center gap-2">
                {busy && <Loader2 className="w-4 h-4 animate-spin" />} {t("common.save")}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
